import {computed, ComputedRef, onMounted, onUnmounted} from 'vue';
import useStore from '../store.ts';
import {ConnectionState} from '../socket/ConnectionState.ts';

export type LoadingPhase = {
    name: string;
    desc: string;
};

// see docs/EXTRACTED_COMMENTS.md#c252
export const LOADING_PHASES: ReadonlyArray<LoadingPhase> = [
    {name: 'CONNECT',      desc: 'Negotiating socket transport'},
    {name: 'AUTHENTICATE', desc: 'Verifying device credentials'},
    {name: 'DISCOVER',     desc: 'Resolving layout manifest'},
    {name: 'BUILD',        desc: 'Building tree · binding channels'},
    {name: 'INGEST',       desc: 'Filling channel data'},
    {name: 'PAINT',        desc: 'Computing element geometry'},
    {name: 'PROVISION',    desc: 'Loading fonts · datasheets'},
    {name: 'READY',        desc: 'Display online'},
] as const;

const PRE_AUTH_STATES: ReadonlySet<ConnectionState> = new Set([
    ConnectionState.Disconnected,
    ConnectionState.Connecting,
]);

const PRE_VERIFIED_STATES: ReadonlySet<ConnectionState> = new Set([
    ConnectionState.Disconnected,
    ConnectionState.Connecting,
    ConnectionState.Authenticating,
    ConnectionState.ConnectedUnverified,
]);

export type LoadingPhaseState = {
    phases: ReadonlyArray<LoadingPhase>;
    isPhaseDeparted: (i: number) => boolean;
    activeIndex: ComputedRef<number>;
    departedCount: ComputedRef<number>;
    progressPercent: ComputedRef<number>;
};

export function useLoadingPhase(): LoadingPhaseState {
    const store = useStore();

    // see docs/EXTRACTED_COMMENTS.md#c253
    const snapshot = (): void => {
        const comps = Object.values(store.components);
        const paintable = comps.filter((c) => !c.flags?.is_datasheet);
        const ingestChannels = store.channelManager.getChannels().map((ch) => ({
            name: String(ch.getName()),
            rows: ch.getRows().length,
            expectedRows: ch.getExpectedRows(),
            pending: ch.isPending(),
        }));
        store.loadingDiagnostics = {
            ingest: {
                pendingChannels: ingestChannels.filter((c) => c.pending).length,
                channels: ingestChannels,
            },
            paint: {
                painted: paintable.filter((c) => c.flags?.geometry_settled).length,
                total: paintable.length,
            },
            provision: {
                fontsReady: store.fonts.mainThreadLoaded.size,
                fontsTotal: store.fonts.required.size,
                datasheetsBuilt: store.datasheets.filter(
                    (id) => store.components[id]?.flags?.calculated_row,
                ).length,
                datasheetsTotal: store.datasheets.length,
            },
        };
    };

    let pollTimer: number | undefined;
    onMounted(() => {
        snapshot();
        pollTimer = window.setInterval(() => {
            if (store.loaded) return;
            snapshot();
        }, 250);
    });
    onUnmounted(() => {
        if (pollTimer !== undefined) clearInterval(pollTimer);
    });

    const isPhaseDeparted = (i: number): boolean => {
        const d = store.loadingDiagnostics;
        switch (LOADING_PHASES[i]?.name) {
            case 'CONNECT':      return !PRE_AUTH_STATES.has(store.connection.state);
            case 'AUTHENTICATE': return !PRE_VERIFIED_STATES.has(store.connection.state);
            // see docs/EXTRACTED_COMMENTS.md#c254
            case 'DISCOVER':     return !store.layoutPending
                                     && Object.keys(store.layoutData ?? {}).length > 0;
            case 'BUILD':        return Object.keys(store.components).length > 0
                                     && store.channelFieldMap.size > 0;
            case 'INGEST':       return !!d?.ingest && d.ingest.pendingChannels === 0;
            case 'PAINT':        return !!d?.paint && d.paint.total > 0
                                     && d.paint.painted >= d.paint.total;
            case 'PROVISION':    return !!d?.provision
                                     && d.provision.fontsReady >= d.provision.fontsTotal
                                     && d.provision.datasheetsBuilt >= d.provision.datasheetsTotal;
            case 'READY':        return store.loaded;
            default:             return false;
        }
    };

    const activeIndex = computed<number>(() => {
        for (let i = 0; i < LOADING_PHASES.length; i++) {
            if (!isPhaseDeparted(i)) return i;
        }
        return LOADING_PHASES.length - 1;
    });

    const departedCount = computed<number>(() => {
        let n = 0;
        for (let i = 0; i < LOADING_PHASES.length; i++) {
            if (isPhaseDeparted(i)) n++;
        }
        return n;
    });

    const progressPercent = computed<number>(
        () => (departedCount.value / LOADING_PHASES.length) * 100,
    );

    return {
        phases: LOADING_PHASES,
        isPhaseDeparted,
        activeIndex,
        departedCount,
        progressPercent,
    };
}
